import { useState, useEffect } from "react";
import { fileService } from "@/services/breeze/FileService";

/**
 * Fetches a short-lived presigned URL for a file. Drop the returned `blobUrl` (kept as the
 * field name for compatibility with existing callers) straight into an <img>/<audio>/<video>
 * `src` — element loads aren't subject to the CORS check that blocks XHR/blob fetches against
 * Spaces.
 */
export function useSecureFile(id: number | null, enabled: boolean = true) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id || !enabled) {
      setBlobUrl(null);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    let cancelled = false;

    fileService
      .getFileSignedUrl(id)
      .then((url) => {
        if (!cancelled) setBlobUrl(url);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to fetch secure file URL:", err);
        setError("Failed to load file");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id, enabled]);

  return { blobUrl, loading, error };
}

export function useSecureImage(id: number | null, isImage: boolean = true) {
  return useSecureFile(id, isImage);
}
